import React, { useCallback, useState } from "react";
import { styled } from "@mui/material/styles";
import { Checkbox } from "@mui/material";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";
import { Site as OptionType } from "interfaces/site";
import { Autocomplete } from "components/Autocomplete";
import { useSitesQuery } from "hooks/queries/useSitesQuery";
import { filterOptions } from "components/CasesTable/Filters/utils/filterOptions";

interface SiteFilterProps {
  selectedSites: OptionType[];
  onChangeSelectedSites: (values: OptionType[]) => void;
  showChips?: boolean;
}

const selectAllOption: OptionType = {
  id: -1,
  name: "Select All",
  reference: "",
  short_name: "",
  bounds: null,
  machines: [],
  create_user: 0,
  change_user: 0,
  create_date: "",
  change_date: ""
};

export const SiteFilter = ({
  selectedSites,
  onChangeSelectedSites,
  showChips = true
}: SiteFilterProps): JSX.Element => {
  const {
    isLoading: sitesLoading,
    isError: sitesError,
    data: sites
  } = useSitesQuery();
  const [options, setOptions] = useState<OptionType[] | undefined>([]);
  const [selectedState, setSelectedState] = useState<OptionType[]>([]);
  const [inputValue, setInputValue] = useState("");

  const onFocus = useCallback(() => {
    if (!sitesLoading && !sitesError) {
      setOptions([selectAllOption, ...sites.results]);
    }
    setSelectedState(selectedSites);
  }, [sites, sitesLoading, sitesError, selectedSites]);

  return (
    <StyledAutocomplete
      label="Site"
      placeholder={showChips ? "Site" : ""}
      loading={sitesLoading}
      error={sitesError}
      showChips={showChips}
      size="small"
      multiple
      autoHighlight
      disableCloseOnSelect
      disableClearable
      autoComplete
      onFocus={onFocus}
      options={options}
      noOptionsText="No results found"
      getOptionLabel={(option: OptionType) => option.name}
      isOptionEqualToValue={(option: OptionType, value: OptionType) =>
        option.name === value.name
      }
      value={selectedSites}
      onInputChange={(
        _event: React.ChangeEvent,
        newInputValue: string
      ): void => {
        if (inputValue !== newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      renderOption={(
        props: any,
        option: OptionType,
        { selected }: { selected: any }
      ) => {
        let { key, ...optionProps } = props;
        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");
        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        const isSelected =
          option.name.toLowerCase() === "select all"
            ? isEqual(filteredSelectedState, filteredNonSelectAllOptions)
            : selected;

        optionProps = {
          ...optionProps,
          "aria-selected": isSelected
        };

        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={isSelected} />
            {option.name}
          </li>
        );
      }}
      onChange={(_event: React.ChangeEvent, values: OptionType[]) => {
        if (!options?.length) {
          return;
        }

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        if (values.some(value => value.name.toLowerCase() === "select all")) {
          // If current selection includes "Select All"
          if (isEqual(filteredSelectedState, filteredNonSelectAllOptions)) {
            // If everything was selected, deselect all
            const selected = selectedState.filter(
              // option => !filteredSelectedState.includes(option)
              option =>
                !filteredSelectedState.some(
                  selected => selected.id === option.id
                )
            );
            onChangeSelectedSites(selected);
            setSelectedState(selected);
          } else {
            const selected = uniqBy(
              [...selectedSites, ...filteredNonSelectAllOptions],
              "id"
            );

            // If not everything was selected, select all
            onChangeSelectedSites(selected);
            setSelectedState(selected);
          }
        } else {
          // Normal selection of individual items
          onChangeSelectedSites(values);
          setSelectedState(values);
        }
      }}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  height: 100%;
`;
